export interface AutoJoin {
  joinName: string;
  toTable: string;
  joinExpression: string;
  joinType: string;
}

export interface TableOrView {
  schema: string;
  name: string;
  kind: "table" | "view";
  columns: string[];
  calculatedFields?: string[];
  autoJoins?: AutoJoin[];
}

export interface FunctionInfo {
  schema: string;
  name: string;
  args: string;
  returnType: string;
}

export interface ForeignKey {
  fromSchema: string;
  fromTable: string;
  fromColumn: string;
  toSchema: string;
  toTable: string;
  toColumn: string;
}

export interface SchemaInfo {
  relations: TableOrView[];
  functions: FunctionInfo[];
  foreignKeys: ForeignKey[];
}

export const SQL_KEYWORDS = [
  "SELECT", "FROM", "WHERE", "AND", "OR", "NOT", "IN", "EXISTS",
  "JOIN", "INNER", "LEFT", "RIGHT", "FULL", "OUTER", "CROSS", "ON",
  "AS", "INSERT", "INTO", "VALUES", "UPDATE", "SET", "DELETE",
  "CREATE", "ALTER", "DROP", "TABLE", "INDEX", "VIEW",
  "GROUP", "BY", "ORDER", "ASC", "DESC", "HAVING",
  "LIMIT", "OFFSET", "UNION", "ALL", "INTERSECT", "EXCEPT",
  "CASE", "WHEN", "THEN", "ELSE", "END",
  "IS", "NULL", "LIKE", "ILIKE", "BETWEEN", "DISTINCT",
  "COUNT", "SUM", "AVG", "MIN", "MAX",
  "COALESCE", "NULLIF", "CAST", "TRUE", "FALSE",
  "WITH", "RECURSIVE", "RETURNING",
  "PRIMARY", "KEY", "FOREIGN", "REFERENCES", "UNIQUE", "CHECK", "DEFAULT",
  "CONSTRAINT", "CASCADE", "RESTRICT",
  "BOOLEAN", "INTEGER", "BIGINT", "SMALLINT", "TEXT", "VARCHAR", "CHAR",
  "NUMERIC", "DECIMAL", "REAL", "FLOAT", "DOUBLE", "PRECISION",
  "DATE", "TIME", "TIMESTAMP", "INTERVAL", "JSONB", "JSON", "UUID",
  "SERIAL", "BIGSERIAL",
];

export interface AliasMap {
  [alias: string]: string;
}

export type ContextKind = "from" | "dot" | "general" | "atField";

export interface CompletionContext {
  kind: ContextKind;
  dotChain?: string[];
  prefix: string; // the partial word typed after the dot or in general position
}

export type SuggestionKind = "schema" | "table" | "view" | "column" | "calculatedField" | "autoJoin" | "fkJoin" | "function" | "keyword";

export interface CompletionSuggestion {
  label: string;
  kind: SuggestionKind;
  detail?: string;
  insertText: string;
  sortText: string;
}

export function findScopeText(fullText: string, offset: number): string {
  let depth = 0;
  let scopeStart = 0;

  for (let i = 0; i < offset; i++) {
    if (fullText[i] === "(") {
      depth++;
      if (depth === 1) scopeStart = i + 1;
    } else if (fullText[i] === ")") {
      if (depth > 0) depth--;
      if (depth === 0) scopeStart = 0;
    }
  }

  if (depth > 0) {
    return fullText.substring(scopeStart, offset);
  }
  return fullText.substring(0, offset);
}

/**
 * The rest of the cursor's scope after it: the text up to the `)` that closes the scope or the
 * statement's `;`, leaving out nested parentheses. Its FROM/JOIN clauses bind aliases the select
 * list before them already uses (`select j.| from job j`).
 */
export function findScopeTextAfter(fullText: string, offset: number): string {
  let depth = 0;
  let out = "";
  for (let i = offset; i < fullText.length; i++) {
    const c = fullText[i];
    if (c === "(") depth++;
    else if (c === ")") {
      if (depth === 0) break;
      depth--;
    } else if (c === ";" && depth === 0) break;
    else if (depth === 0) out += c;
  }
  return out;
}

export function buildAliasMap(scopeText: string, relations: TableOrView[]): AliasMap {
  const map: AliasMap = {};

  const pattern = /\b(?:FROM|JOIN)\s+([\w.]+)(?:\s+(?:AS\s+)?(\w+))?/gi;
  let m;
  while ((m = pattern.exec(scopeText)) !== null) {
    const tableRef = m[1];
    const alias = m[2] || tableRef.split(".").pop()!;
    if (m[2] && /^(SELECT|WHERE|JOIN|INNER|LEFT|RIGHT|FULL|CROSS|ON|SET|ORDER|GROUP|HAVING|LIMIT|UNION|INTERSECT|EXCEPT)\s*$/i.test(m[2])) {
      map[tableRef.split(".").pop()!.toLowerCase()] = resolveTableRef(tableRef, relations);
    } else {
      map[alias.toLowerCase()] = resolveTableRef(tableRef, relations);
    }
  }

  return map;
}

export function resolveTableRef(ref: string, relations: TableOrView[]): string {
  if (ref.includes(".")) return ref;
  const match = relations.find(r => r.name.toLowerCase() === ref.toLowerCase());
  if (match) return `${match.schema}.${match.name}`;
  return ref;
}

export function findRelation(qualifiedName: string, relations: TableOrView[]): TableOrView | undefined {
  const parts = qualifiedName.split(".");
  if (parts.length === 2) {
    return relations.find(r => r.schema.toLowerCase() === parts[0].toLowerCase() && r.name.toLowerCase() === parts[1].toLowerCase());
  }
  return relations.find(r => r.name.toLowerCase() === qualifiedName.toLowerCase());
}

export function resolveDotChain(chain: string[], aliasMap: AliasMap, relations: TableOrView[]): TableOrView | undefined {
  if (chain.length === 0) return undefined;

  const firstSeg = chain[0].toLowerCase();
  const qualifiedName = aliasMap[firstSeg];
  if (!qualifiedName) return undefined;

  let current: TableOrView | undefined = findRelation(qualifiedName, relations);
  if (!current) return undefined;

  for (let i = 1; i < chain.length; i++) {
    const seg = chain[i].toLowerCase();
    const autoJoin: AutoJoin | undefined = current.autoJoins?.find(j => j.joinName.toLowerCase() === seg);
    if (!autoJoin) return undefined;
    const target: TableOrView | undefined = relations.find(r => r.name.toLowerCase() === autoJoin.toTable.toLowerCase());
    if (!target) return undefined;
    current = target;
  }

  return current;
}

/**
 * Parse "text before cursor" (everything up to the cursor on the current line)
 * and "full text up to cursor" (multi-line) to determine completion context.
 *
 * The `|` in the input marks cursor position for testing convenience,
 * but callers pass pre-split strings.
 */
export function getCompletionContext(textBeforeCursorOnLine: string, fullTextUpToCursor: string): CompletionContext {
  // @field placeholder: "@<partial>" — the write's input columns bound at
  // validation time (SELECT ... WHERE uid = @worker_uid). Detected before the
  // dot check so "@col" isn't mistaken for a bare word.
  const atMatch = textBeforeCursorOnLine.match(/@(\w*)$/);
  if (atMatch) {
    return { kind: "atField", prefix: atMatch[1] || "" };
  }

  // Check for dot chain: "alias.col", "alias.", "schema.table."
  const dotMatch = textBeforeCursorOnLine.match(/((?:\w+\.)+)(\w*)$/);
  if (dotMatch) {
    const chain = dotMatch[1].replace(/\.$/, "").split(".");
    return {
      kind: "dot",
      dotChain: chain,
      prefix: dotMatch[2] || "",
    };
  }

  // Get the current partial word (last word being typed)
  const wordMatch = textBeforeCursorOnLine.match(/(\w+)$/);
  const currentWord = wordMatch ? wordMatch[1] : "";

  // Check if after FROM/JOIN keywords
  const trimmed = fullTextUpToCursor.replace(/\s+/g, " ").trimEnd();
  if (/\b(?:FROM|JOIN)\s*$/i.test(trimmed)) {
    return { kind: "from", prefix: "" };
  }

  // Check if we're mid-word after FROM/JOIN (e.g. "FROM proc")
  const beforeWord = trimmed.substring(0, trimmed.length - currentWord.length).trimEnd();
  if (/\b(?:FROM|JOIN)\s*$/i.test(beforeWord)) {
    return { kind: "from", prefix: currentWord };
  }

  return { kind: "general", prefix: currentWord };
}

/**
 * Given a completion context, schema info, and the full text, produce suggestions.
 * This is the pure logic — no Monaco dependency.
 */
export function getSuggestions(
  ctx: CompletionContext,
  schema: SchemaInfo,
  fullText: string,
  cursorOffset: number,
  /**
   * Tables in scope WITHOUT appearing in a FROM clause — for bare-expression
   * editors (a calculated field, a row filter) where the surrounding
   * statement supplies the table. Each name is seeded into the alias map as
   * itself, so its columns complete in general context and its auto-joins
   * resolve in dot-chains (worker.label). Explicit FROM/JOIN aliases win.
   */
  implicitTables?: string[],
  /**
   * Extra alias -> table-name bindings that are ALWAYS valid in this editor,
   * regardless of a FROM clause. For a calculated field the firewall accepts
   * the bare table name (`fileset.col`) AND `outer.col` (correlated-subquery
   * form) — seeding both here means `outer.` / `fileset.` dot-completion lists
   * the table's full column/calc/join set (the "show me everything" path).
   */
  extraAliases?: Record<string, string>,
  /**
   * When true, general (non-dot) context offers ONLY the alias names and
   * keywords/functions — NOT bare column names. For a join expression every
   * field ref must be qualified (left.<col> = right.<col>); a bare column is
   * meaningless, so we don't offer it.
   */
  suppressBareColumns?: boolean,
  /**
   * The write's input columns, offered after `@` (validation queries:
   * `@worker_uid` binds the row's worker_uid at validation time). Typically
   * the validation's own table columns.
   */
  atFieldColumns?: string[],
): CompletionSuggestion[] {
  const suggestions: CompletionSuggestion[] = [];

  // @field context: offer the input columns as @<column> placeholders.
  if (ctx.kind === "atField") {
    for (const col of atFieldColumns ?? []) {
      suggestions.push({
        label: `@${col}`,
        kind: "column",
        detail: "input field (bound at write time)",
        // The `@` is already typed; insert just the column name after it.
        insertText: col,
        sortText: `0_${col}`,
      });
    }
    return suggestions;
  }

  const scopeText = findScopeText(fullText, cursorOffset);
  const aliasMap = buildAliasMap(scopeText + " " + findScopeTextAfter(fullText, cursorOffset), schema.relations);
  for (const t of implicitTables ?? []) {
    const key = t.toLowerCase();
    if (!(key in aliasMap)) aliasMap[key] = resolveTableRef(t, schema.relations);
    // The implicit table's AUTO-JOIN NAMES are also referenceable bare in a
    // firewall expression (worker.label on processrun) — alias each join name
    // to its target relation so a join-name-first dot-chain resolves.
    const rel = findRelation(aliasMap[key], schema.relations);
    for (const aj of rel?.autoJoins ?? []) {
      const jkey = aj.joinName.toLowerCase();
      if (!(jkey in aliasMap)) {
        aliasMap[jkey] = resolveTableRef(aj.toTable, schema.relations);
      }
    }
  }
  // Always-valid aliases (outer, the bare table name) -> their relation.
  for (const [alias, tableName] of Object.entries(extraAliases ?? {})) {
    const key = alias.toLowerCase();
    if (!(key in aliasMap)) aliasMap[key] = resolveTableRef(tableName, schema.relations);
  }

  if (ctx.kind === "from") {
    const schemas = new Set(schema.relations.map(r => r.schema));
    for (const s of schemas) {
      suggestions.push({
        label: s,
        kind: "schema",
        detail: "schema",
        insertText: s,
        sortText: `0_${s}`,
      });
    }

    for (const rel of schema.relations) {
      suggestions.push({
        label: rel.name,
        kind: rel.kind === "view" ? "view" : "table",
        detail: `${rel.schema} (${rel.kind})`,
        insertText: `${rel.schema}.${rel.name}`,
        sortText: `1_${rel.name}`,
      });
    }
  } else if (ctx.kind === "dot" && ctx.dotChain) {
    const chain = ctx.dotChain;
    const firstSeg = chain[0].toLowerCase();

    if (chain.length === 1) {
      const schemaRelations = schema.relations.filter(r => r.schema.toLowerCase() === firstSeg);
      if (schemaRelations.length > 0) {
        for (const rel of schemaRelations) {
          suggestions.push({
            label: rel.name,
            kind: rel.kind === "view" ? "view" : "table",
            detail: `${rel.schema} (${rel.kind})`,
            insertText: rel.name,
            sortText: `0_${rel.name}`,
          });
        }
        return suggestions;
      }
    }

    const rel = resolveDotChain(chain, aliasMap, schema.relations);
    if (rel) {
      for (const col of rel.columns) {
        suggestions.push({
          label: col,
          kind: "column",
          detail: rel.name,
          insertText: col,
          sortText: `0_${col}`,
        });
      }

      if (rel.calculatedFields) {
        for (const cf of rel.calculatedFields) {
          suggestions.push({
            label: cf,
            kind: "calculatedField",
            detail: `${rel.name} (calculated)`,
            insertText: cf,
            sortText: `0_${cf}`,
          });
        }
      }

      if (rel.autoJoins) {
        for (const aj of rel.autoJoins) {
          suggestions.push({
            label: aj.joinName,
            kind: "autoJoin",
            detail: `→ ${aj.toTable} (${aj.joinType} join)`,
            insertText: aj.joinName,
            sortText: `0_${aj.joinName}`,
          });
        }
      }

      const dotPrefix = chain.join(".");
      const relKey = `${rel.schema}.${rel.name}`.toLowerCase();
      for (const fk of schema.foreignKeys) {
        const fromKey = `${fk.fromSchema}.${fk.fromTable}`.toLowerCase();
        const toKey = `${fk.toSchema}.${fk.toTable}`.toLowerCase();
        if (fromKey === relKey) {
          suggestions.push({
            label: `→ ${fk.toTable}`,
            kind: "fkJoin",
            detail: `JOIN via ${fk.fromColumn} → ${fk.toTable}.${fk.toColumn}`,
            insertText: `${fk.fromColumn} JOIN ${fk.toSchema}.${fk.toTable} ON ${dotPrefix}.${fk.fromColumn} = ${fk.toTable}.${fk.toColumn}`,
            sortText: `2_${fk.toTable}`,
          });
        } else if (toKey === relKey) {
          suggestions.push({
            label: `→ ${fk.fromTable}`,
            kind: "fkJoin",
            detail: `JOIN via ${fk.toColumn} ← ${fk.fromTable}.${fk.fromColumn}`,
            insertText: `${fk.toColumn} JOIN ${fk.fromSchema}.${fk.fromTable} ON ${dotPrefix}.${fk.toColumn} = ${fk.fromTable}.${fk.fromColumn}`,
            sortText: `2_${fk.fromTable}`,
          });
        }
      }
    }
  } else {
    const seenCols = new Set<string>();
    // Offer the always-valid aliases themselves (outer, the table name) so a
    // user can type `outer.`/`fileset.` to see everything — the discoverable
    // "show me all fields" path.
    for (const alias of Object.keys(extraAliases ?? {})) {
      suggestions.push({
        label: alias,
        kind: "table",
        detail: "alias · type . to list all columns/fields/joins",
        insertText: alias,
        sortText: `0_${alias}`,
      });
    }
    // The implicit CONTEXT tables' own columns complete UNQUALIFIED — in a
    // bare expression `status` is the normal spelling, `processrun.status`
    // the exception. Joined/aliased relations (and the always-valid aliases)
    // stay unqualified too so they don't force-prefix every column.
    const bareAliases = new Set([
      ...(implicitTables ?? []).map((t) => t.toLowerCase()),
      ...Object.keys(extraAliases ?? {}).map((a) => a.toLowerCase()),
    ]);
    // Join expressions require qualified refs (left./right.); don't offer bare
    // columns at all — only the alias names (already pushed above) + keywords.
    for (const [alias, qualifiedName] of suppressBareColumns
      ? []
      : Object.entries(aliasMap)) {
      const rel = findRelation(qualifiedName, schema.relations);
      if (rel) {
        const qualify = Object.keys(aliasMap).length > 1 && !bareAliases.has(alias);
        for (const col of rel.columns) {
          const label = qualify ? `${alias}.${col}` : col;
          if (seenCols.has(label)) continue;
          seenCols.add(label);
          suggestions.push({
            label,
            kind: "column",
            detail: rel.name,
            insertText: label,
            sortText: `1_${col}`,
          });
        }
      }
    }

    for (const fn of schema.functions) {
      suggestions.push({
        label: fn.name,
        kind: "function",
        detail: `(${fn.args}) → ${fn.returnType}`,
        insertText: `${fn.name}(`,
        sortText: `2_${fn.name}`,
      });
    }

    for (const kw of SQL_KEYWORDS) {
      suggestions.push({
        label: kw,
        kind: "keyword",
        insertText: kw,
        sortText: `3_${kw}`,
      });
    }
  }

  return suggestions;
}

/**
 * Helper for tests: parse an input string with `|` marking cursor position,
 * compute context and suggestions.
 */
export function completionsAt(input: string, schema: SchemaInfo): CompletionSuggestion[] {
  const cursorPos = input.indexOf("|");
  if (cursorPos === -1) throw new Error("Input must contain | to mark cursor position");

  const fullText = input.replace("|", "");
  const textUpToCursor = fullText.substring(0, cursorPos);

  const lastNewline = textUpToCursor.lastIndexOf("\n");
  const lineTextBeforeCursor = lastNewline === -1 ? textUpToCursor : textUpToCursor.substring(lastNewline + 1);

  const ctx = getCompletionContext(lineTextBeforeCursor, textUpToCursor);
  return getSuggestions(ctx, schema, fullText, cursorPos);
}

/**
 * Like completionsAt but filters by prefix (simulating what Monaco does).
 */
export function filteredCompletionsAt(input: string, schema: SchemaInfo): CompletionSuggestion[] {
  const cursorPos = input.indexOf("|");
  if (cursorPos === -1) throw new Error("Input must contain | to mark cursor position");

  const fullText = input.replace("|", "");
  const textUpToCursor = fullText.substring(0, cursorPos);

  const lastNewline = textUpToCursor.lastIndexOf("\n");
  const lineTextBeforeCursor = lastNewline === -1 ? textUpToCursor : textUpToCursor.substring(lastNewline + 1);

  const ctx = getCompletionContext(lineTextBeforeCursor, textUpToCursor);
  const all = getSuggestions(ctx, schema, fullText, cursorPos);

  if (!ctx.prefix) return all;
  const lower = ctx.prefix.toLowerCase();
  return all.filter(s => s.label.toLowerCase().startsWith(lower));
}
